import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {handleExcelReport} from '../../common/components/commercialTable/exportUtils/handleExport';
import {sendCommercialUsersData, setEditableRowIndex, updateCommercialUsers} from '../actions/edit';
import {addNewCommercialUser} from '../actions/create';
import {getAllAgencies, fetchDealers} from '../../dealer/actions/actions';
import {cleanCommercialUsersReducer, fetchCommercialUsers} from '../actions/get';
import {setShowThanksModal} from '../actions/modals';
import * as constants from './constants';
import {getColumns} from './columns';
import {Actions as MessageActions} from '../../common/messageBox/actions';

export const useCommercialUsers = () => {
  const {
    data, agenciesByDealer, showThanksModal, loading,
    newRows, deletedRows, modifiedRows, invalidRowsValidation, validationMessage,
  } = useSelector((state) => state.commercialUsers);
  const {'list': dealerList} = useSelector((state) => state.dealer);
  const sessionInfo = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const [showUpdateBaseModal, setShowUpdateBaseModal] = useState(false);
  const [dataToUpdate, setDataToUpdate] = useState([]);

  const [filteredData, setFilteredData] = useState(data);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const tableData = useMemo(() => data, [data]);
  const columns = useMemo(() => getColumns(
    dispatch, {
      agenciesByDealer,
      dealerList,
      newRows,
      sessionInfo,
    }
  ), [sessionInfo, dealerList, agenciesByDealer, Object.keys(newRows).length]);
  
  const downloadFile = (content, filename) => {
    const element = document.createElement('a');
    const file = new Blob([content], {'type': 'text/csv'});
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
  };

  const addNewUser = () => {
    const id = new Date().getTime();

    const defaultDealer = sessionInfo.isUserFromTDE ?
      '' : (dealerList.find((dlr) => dlr.identification === sessionInfo.dealerId)?.name ?? '');

    dispatch(addNewCommercialUser(id, defaultDealer));
    dispatch(setEditableRowIndex(0));
  };

  const downloadCSV = () => {
    const timestamp = new Date().getTime();

    const rowsData = data.filter((row) => row.exportado === 'NO').map((row) => (
      `${0},\
      ${row.estado === 'SI' ? 0 : 1},\
      ${row.usuario ? row.usuario : ''},\
      ${row.usuario ? row.usuario : ''},\
      ${row.nombres ? row.nombres : ''},\
      ${row.apellidos ? row.apellidos : ''},\
      ${row.correo ? row.correo : ''},\
      ${'EC'},\
      ${row.ciudad ? `${row.ciudad.charAt(0).toUpperCase()}${row.ciudad.slice(1).toLowerCase()}` : ''},\
      ${'es'},\
      ${constants.TOYOTA_ACADEMY_ADMINISTRATOR},\
      ${constants.institutionMapper[row.concesionario]?.ogridnumber ?? row.concesionario},\
      ${row.cargo ? row.cargo : ''},\
      ${constants.workPositions[row.cargo]},\
      ${constants.institutionMapper[row.concesionario]?.institution ?? row.concesionario},\
      ${row.departamento ? row.departamento : ''},\
      ${row.telefono ? `+593${row.telefono?.slice(1)}` : ''}\
      `)
    );

    const content = `${constants.csvColumns.join(',')}\n${rowsData.join('\n')}`;

    downloadFile(content, `reporte ${timestamp}.csv`);

    const justExportedRows = {
      ...(data.filter((row) => row.exportado === 'NO').reduce((pv, cv) => ({
        ...pv,
        [cv.id]: {
          'exportado': 'S',
        },
      }), {})),
    };

    dispatch(sendCommercialUsersData({'newRows': {}, 'deletedRows': {}, 'modifiedRows': justExportedRows}));
  };

  const downloadExcel = () => {
    const timestamp = new Date().getTime();

    handleExcelReport(tableData, columns.filter((col) => col.title), null, `reporte ${timestamp}.xlsx`);
  };

  const handleBotonGuardar = () => {
    const result = Object.keys(newRows).filter((key) => {
      const row = newRows[key];
      return [
        'concesionario', 'agencia', 'departamento', 'correo',
        'nombres', 'apellidos', 'cargo', 'telefono',
      ].filter((field) => !row[field]).length > 0;
    });

    if (result.length > 0) {
      dispatch(MessageActions.addMessageBoxError('Debe ingresar todos los datos.'));
      return;
    }

    setShowConfirmationModal(true);
  };

  const saveChanges = () => {
    setShowConfirmationModal(false);
    dispatch(sendCommercialUsersData({newRows, modifiedRows, deletedRows}));
    dispatch(setShowThanksModal(true));
  };

  const hideThanksModal = () => {
    dispatch(setShowThanksModal(false));
  };

  const handleShowModalButton = (value) => {
    setShowUpdateBaseModal(value);
    if (value === false) {
      setDataToUpdate([]);
    }
  };
  
  const handleDataToUpdate = (data) => {
    setDataToUpdate(data);
  };

  const confirmUpdate = () => {
    dispatch(MessageActions.addMessageBoxWarning('Actualizando base de datos de usuarios...'));
    dispatch(updateCommercialUsers(dataToUpdate));
  };

  useEffect(() => {
    dispatch(cleanCommercialUsersReducer());
    dispatch(fetchCommercialUsers());
    dispatch(fetchDealers('dealers-vehicles'));
    dispatch(getAllAgencies());
  }, []);

  useEffect(() => {
    if (validationMessage && validationMessage !== 'Error') {
      setShowUpdateBaseModal(false);
      dispatch(cleanCommercialUsersReducer());
      dispatch(fetchCommercialUsers());
    }
  }, [validationMessage]);

  return {
    'values': {
      loading, columns, filteredData, showConfirmationModal, showThanksModal, dataToUpdate, validationMessage,
      'isTDE': sessionInfo.isUserFromTDE,
      'data': tableData,
      'dealerName': dealerList.find((dealer) => dealer.identification === sessionInfo.dealerId)?.name ?? '',
      'showUpdateBaseModal': showUpdateBaseModal,
      'invalidRowsValidation': invalidRowsValidation,
    },
    'functions': {
      addNewUser, downloadExcel, downloadCSV, hideThanksModal,
      setFilteredData, setShowConfirmationModal, saveChanges,
      confirmUpdate,
    },
    'handlers': {
      handleBotonGuardar,
      handleShowModalButton,
      handleDataToUpdate,
    },
  };
};
